<template>
  <div
    class="task-row group relative flex items-center gap-3 px-4 py-3.5 sm:py-3 cursor-pointer transition-colors duration-150 active:bg-gray-50 dark:active:bg-white/[0.03] sm:hover:bg-blue-50/60 dark:sm:hover:bg-blue-500/[0.06]"
    @click="emit('open', task)"
  >
    <span
      v-if="reorderable"
      class="task-row-handle flex-shrink-0 -ml-1 flex items-center justify-center w-4 h-5 text-gray-300 dark:text-gray-600 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-150 cursor-grab active:cursor-grabbing"
      @click.stop
      aria-hidden="true"
    >
      <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
        <circle cx="7" cy="5" r="1.3"/><circle cx="13" cy="5" r="1.3"/>
        <circle cx="7" cy="10" r="1.3"/><circle cx="13" cy="10" r="1.3"/>
        <circle cx="7" cy="15" r="1.3"/><circle cx="13" cy="15" r="1.3"/>
      </svg>
    </span>

    <button
      type="button"
      @click.stop="emit('toggle', task, $event)"
      @mouseenter="hovering = true"
      @mouseleave="hovering = false"
      class="flex-shrink-0 w-6 h-6 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center transition-all duration-150 ease-out active:scale-90 sm:hover:scale-110"
      :style="checkboxStyle"
      :aria-label="isDone ? 'Mark as not done' : 'Mark as done'"
    >
      <Transition name="check-pop">
        <svg v-if="isDone" class="w-3.5 h-3.5 sm:w-3 sm:h-3 text-white" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M1.5 5l2.5 2.5 4.5-4.5"/>
        </svg>
      </Transition>
    </button>

    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-1.5">
        <svg v-if="task.is_recurring" class="w-3 h-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
        </svg>
        <p class="text-[15px] sm:text-sm truncate transition-colors duration-150" :class="isDone ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-800 dark:text-gray-100 sm:group-hover:text-gray-900 dark:sm:group-hover:text-white'">
          {{ task.title }}
        </p>
      </div>
      <p v-if="descriptionPreview" class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{{ descriptionPreview }}</p>
    </div>

    <div class="flex-shrink-0 flex items-center gap-1 sm:gap-1.5" @click.stop>
      <!-- Public link indicator. Deliberately not a button: acting on a share
           belongs on the task's own page, this is only telling you the task
           is out in the world. -->
      <span
        v-if="task.shared_publicly"
        class="flex items-center justify-center w-6 h-6 rounded-md text-blue-500 dark:text-blue-400"
        title="This task has a public link"
        aria-label="Shared publicly"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
        </svg>
      </span>
      <TaskPriorityDropdown :priority="task.priority" @update="(p) => emit('update-priority', task, p)" />
      <TaskDueDateDropdown :due-at="task.due_at" :overdue="task.is_overdue && !isDone" @update="(d) => emit('update-due-date', task, d)" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { stripHtml } from './stripHtml.js'
import TaskPriorityDropdown from './TaskPriorityDropdown.vue'
import TaskDueDateDropdown from './TaskDueDateDropdown.vue'

const props = defineProps({
  task: { type: Object, required: true },
  // Only true for the "Today"/"This week" checklist chips — see
  // TasksChecklist.vue and useChecklistOrder.js. Gates whether the drag
  // handle renders at all, not just whether dragging works, so other chips'
  // rows keep their original layout unchanged. Deliberately not named
  // `draggable` — that collides in spirit with the native HTML attribute of
  // the same name and with the imported `vuedraggable` component in the
  // parent template.
  reorderable: { type: Boolean, default: false },
})
// 'toggle' carries the raw click MouseEvent too — TasksChecklist.vue reads
// its clientX/clientY to anchor the completion confetti burst at the
// checkbox, without this row needing to know anything about that effect.
const emit = defineEmits(['open', 'toggle', 'update-priority', 'update-due-date'])

const isDone = computed(() => props.task.status === 'completed')
const descriptionPreview = computed(() => stripHtml(props.task.description))

// Hover fills the circle with a soft preview of the task's own color before
// it's actually completed — a visual hint that clicking commits to that
// color, not just a generic checkmark.
const hovering = ref(false)
const checkboxStyle = computed(() => {
  const color = props.task.color_css_code || '#2563eb'
  return {
    backgroundColor: isDone.value ? color : hovering.value ? `${color}26` : 'transparent',
    borderColor: color,
    boxShadow: isDone.value ? `0 0 0 3px ${color}22` : 'none',
  }
})
</script>

<style scoped>
/* Bounce-in for the checkmark — status flips optimistically (see
   TasksChecklist.vue's toggle()), so this plays the instant the click
   happens, not after the PATCH resolves. */
.check-pop-enter-active {
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.15s ease;
}
.check-pop-enter-from {
  transform: scale(0);
  opacity: 0;
}
.check-pop-leave-active {
  transition: transform 0.15s ease, opacity 0.15s ease;
}
.check-pop-leave-to {
  transform: scale(0);
  opacity: 0;
}
</style>
